/**
 * N103 (Alana, 27/07): "um botão que a pessoa clica se quiser ver os detalhes —
 * endereço do hospital, e-mails e telefones dos responsáveis dos pontos", dito
 * junto com o pedido de não deixar a tela de agendamento poluída.
 *
 * O N75 atendeu a primeira metade (agregar os e-mails) mas deixou a lista inline
 * e sempre aberta — era ela que poluía. Aqui a mesma informação, mais endereço e
 * telefones, fica atrás de um botão, fechado por padrão.
 *
 * Duas decisões que valem registro:
 *
 * 1. `<details>/<summary>` nativo em vez de modal com estado: sem useState, e
 *    teclado/leitor de tela funcionam sem escrever nada.
 * 2. NÃO reusa o `HospitalConsultaModal` da tela de Escopo, apesar de mostrar
 *    dados parecidos. Aquele modal busca `getEstabelecimento` +
 *    `getLocaisVinculados` para UM cliente; aqui a aba já tem hospital, clientes
 *    e pontos carregados, e a contagem pode ter VÁRIOS clientes. Reusar custaria
 *    duas chamadas repetidas e uma escolha de cliente que não existe neste fluxo.
 */
import React from 'react';
import { Building2, ChevronDown, MapPin, Phone } from 'lucide-react';

import { EmailsResponsaveisField } from './EmailsResponsaveisField';
import type { Estabelecimento, LocalContagem } from '../../types/estabelecimento';
import type { Cliente } from '../../services/clienteService';

interface Props {
  hospital?: Estabelecimento;
  clientes: Cliente[];
  locais: LocalContagem[];
}

function ou(valor?: string | null): string {
  return valor?.trim() || 'Não informado';
}

/** "(11) 1234-5678 ramal 20" — o ramal só aparece se existir. */
function telefoneCom(ramal?: string, telefone?: string): string {
  const base = telefone?.trim();
  if (!base) return 'Não informado';
  return ramal?.trim() ? `${base} ramal ${ramal.trim()}` : base;
}

export const HospitalContatosSection: React.FC<Props> = ({ hospital, clientes, locais }) => {
  const endereco = hospital
    ? [hospital.endereco, hospital.bairro, hospital.cidade, hospital.estado, hospital.cep]
        .map((p) => p?.trim())
        .filter(Boolean)
        .join(' - ')
    : '';

  return (
    <details className="group col-span-full rounded-lg border border-gray-200 dark:border-gray-700">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/60 [&::-webkit-details-marker]:hidden">
        <Building2 size={16} />
        Hospital e contatos
        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
          (endereço, e-mails e telefones dos responsáveis)
        </span>
        <ChevronDown
          size={16}
          className="ml-auto shrink-0 transition-transform group-open:rotate-180"
        />
      </summary>

      <div className="space-y-4 border-t border-gray-200 p-3 dark:border-gray-700">
        {!hospital ? (
          <p className="text-sm italic text-gray-500 dark:text-gray-400">
            Selecione o hospital para ver endereço e contatos.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <span className="flex items-center gap-1 text-xs font-medium uppercase text-gray-500">
                <MapPin size={13} />
                Endereço
              </span>
              <p className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{ou(endereco)}</p>
            </div>
            <div>
              <span className="flex items-center gap-1 text-xs font-medium uppercase text-gray-500">
                <Phone size={13} />
                Telefone do hospital
              </span>
              <p className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                {telefoneCom(hospital.ramal, hospital.telefone)}
              </p>
            </div>
            <div>
              <span className="text-xs font-medium uppercase text-gray-500">Contato</span>
              <p className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                {ou(hospital.contato)}
              </p>
            </div>
          </div>
        )}

        <div>
          <h4 className="mb-1.5 text-xs font-medium uppercase text-gray-500">
            Responsáveis dos pontos de contagem ({locais.length})
          </h4>
          {locais.length === 0 ? (
            <p className="text-sm italic text-gray-500 dark:text-gray-400">
              Nenhum ponto de contagem selecionado.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-300 text-left text-xs uppercase text-gray-500 dark:border-gray-600">
                    <th className="py-1.5 pr-3 font-medium">Ponto</th>
                    <th className="py-1.5 pr-3 font-medium">Responsável</th>
                    <th className="py-1.5 pr-3 font-medium">Telefone</th>
                    <th className="py-1.5 font-medium">E-mail</th>
                  </tr>
                </thead>
                <tbody>
                  {locais.map((l) => (
                    <tr key={l.id} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-1.5 pr-3">{ou(l.local)}</td>
                      <td className="py-1.5 pr-3">{ou(l.responsavel)}</td>
                      <td className="py-1.5 pr-3">{telefoneCom(l.ramal, l.telefone)}</td>
                      <td className="break-all py-1.5">{ou(l.email)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* N75 continua aqui dentro, com o "Copiar todos" que ela usa. */}
        <EmailsResponsaveisField hospital={hospital} clientes={clientes} locais={locais} />
      </div>
    </details>
  );
};
